import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DefaultLayout from "../../layout/DefaultLayout";
import Loading from "../../components/Loading";
import Alert from "../../components/Alert";
import creditoService from "../../services/creditoService";
import { useGlobalState } from "../../state";
import { formatShortDate, toCurrency } from "../../utils/formaters";
import { FaShareAlt, FaCheck, FaCloudDownloadAlt } from "react-icons/fa";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";

const STORAGE_KEY = "resultadoSimulacion";

const base64ToBlob = (base64: string, mime = "application/pdf") => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
};

const downloadBase64File = (base64: string, filename: string, mime = "application/pdf") => {
    try {
        const blob = base64ToBlob(base64, mime);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "reporte.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (e) {
        console.error("Error downloading file", e);
    }
};

const ResultadoSimular: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user] = useGlobalState("user");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [warning, setWarning] = useState<string | undefined>(undefined);
    const [resultadoSimulacion, setResultadoSimulacion] = useState<any | undefined>(undefined);

    // read query param "data"
    const query = new URLSearchParams(location.search);
    const raw = query.get("data");
    let parsedData: any = null;
    try {
        parsedData = raw ? JSON.parse(decodeURIComponent(raw)) : null;
    } catch {
        parsedData = null;
    }

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            setError(undefined);
            setWarning(undefined);

            if (user === undefined) {
                // still checking auth
                return;
            }
            if (!user) {
                navigate("/auth/signin");
                return;
            }

            if (!parsedData) {
                setError("Parámetros inválidos para la simulación.");
                setLoading(false);
                return;
            }

            try {
                const res: any = await creditoService.simular(parsedData);
                if (res?.success) {
                    setResultadoSimulacion(res.data);
                    try {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data));
                    } catch { }
                    if (res.messages && res.messages.length > 0) {
                        setWarning(res.messages[0]);
                    }
                } else {
                    setError(res?.errors?.[0] ?? "Ocurrió un error en la simulación.");
                }
            } catch (ex: any) {
                setError(ex?.message ?? "Ocurrió un error al ejecutar la simulación");
            } finally {
                setLoading(false);
            }
        };

        fetch();
        // re-run when user becomes available
    }, [user]);

    // Diagnostic: capturar promesas rechazadas/global errors para ver stack y origen
    useEffect(() => {
        const onUnhandledRejection = (ev: PromiseRejectionEvent) => {
            console.error("UnhandledPromiseRejection detected in ResultadoSimular:", ev.reason);
            // Si prefieres mostrar al usuario:
            const msg = ev.reason?.message ?? (typeof ev.reason === "string" ? ev.reason : "Error desconocido");
            setError(`Error inesperado: ${msg}`);
        };
        const onError = (ev: ErrorEvent) => {
            console.error("Global error detected in ResultadoSimular:", ev.error ?? ev.message);
            setError(ev.error?.message ?? ev.message ?? "Error de ejecución");
        };
        window.addEventListener("unhandledrejection", onUnhandledRejection as any);
        window.addEventListener("error", onError as any);
        return () => {
            window.removeEventListener("unhandledrejection", onUnhandledRejection as any);
            window.removeEventListener("error", onError as any);
        };
    }, []);

    const handleDownloadPdf = async () => {
        if (!parsedData) return;
        setLoading(true);
        setError(undefined);
        try {
            const payload = { ...parsedData, generarReporte: true };
            const res: any = await creditoService.simular(payload);
            if (res?.success && res.data?.reporteResponse?.reporte) {
                // reporte probably base64
                const base64 = res.data.reporteResponse.reporte;
                const filename = res.data.reporteResponse.nombreReporte || "Simulacion_Credito.pdf";
                downloadBase64File(base64, filename, "application/pdf");
            } else {
                setError(res?.errors?.[0] ?? "No se pudo generar el PDF");
            }
        } catch (ex: any) {
            setError(ex?.message ?? "Ocurrió un error al generar el PDF");
        } finally {
            setLoading(false);
        }
    };

    // Mejor manejar errores aquí y guardar resultado antes de navegar.
    const handleSolicitar = async () => {
        try {
            // Guardar resultado para la pantalla de carga de documentos (si la usa)
            if (resultadoSimulacion) {
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(resultadoSimulacion));
                } catch { }
            }
            navigate("/prestamos/carga-documentos");
        } catch (ex: any) {
            console.error("Error al iniciar solicitud de préstamo:", ex);
            setError(ex?.message ?? "Ocurrió un error al iniciar la solicitud");
        }
    };

    return (
        <DefaultLayout>
            <Breadcrumb pageName="Resultado de la simulación" />

            <div className="md:p-6">
                <div className="md:max-w-4xl mx-auto bg-white dark:bg-boxdark rounded p-4">
                    {loading && <Loading statusText="Procesando..." />}

                    {error && <Alert type="failure" message={error} />}

                    {warning && (
                        <div className="bg-yellow-100 text-yellow-900 p-3 rounded mb-4 text-sm text-center">
                            {warning}
                        </div>
                    )}

                    {!loading && resultadoSimulacion && (
                        <>
                            <p className="text-sm text-gray-500 mb-2">Estos son los datos estimados según tu simulación:</p>

                            <div className="bg-gray-50 p-4 rounded mb-4">
                                <div className="flex justify-between items-start">
                                    <div className="text-base font-semibold">{resultadoSimulacion.lineaDeCredito?.nombre}</div>
                                    <div className="text-sm text-gray-600">
                                        TNA: {resultadoSimulacion.lineaDeCredito?.tasa?.valor + resultadoSimulacion.lineaDeCredito?.tasa?.margenAdicional}%
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <div className="grid grid-cols-1 gap-3">
                                        <div className="flex justify-between">
                                            <div className="text-sm">Monto solicitado:</div>
                                            <div className="text-sm font-semibold">{toCurrency(resultadoSimulacion.monto)}</div>
                                        </div>

                                        <div className="flex justify-between">
                                            <div className="text-sm">Monto a recibir:</div>
                                            <div className="text-sm font-semibold">{toCurrency(resultadoSimulacion.montoNetoARecibir)}</div>
                                        </div>

                                        <div className="flex justify-between">
                                            <div className="text-sm">Número de cuotas:</div>
                                            <div className="text-sm font-semibold">{resultadoSimulacion.cantidadDeCuotas}</div>
                                        </div>

                                        <div className="flex justify-between">
                                            <div className="text-sm">Valor cuota promedio:</div>
                                            <div className="text-sm font-semibold">{toCurrency(resultadoSimulacion.cuotaPromedio)}</div>
                                        </div>

                                        <div className="flex justify-between">
                                            <div className="text-sm">Fecha estimada de la primera cuota:</div>
                                            <div className="text-sm font-semibold">{formatShortDate(resultadoSimulacion.fechaPrimeraCuota)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded mb-4">
                                <h2 className="text-lg font-medium mb-3">Gastos</h2>
                                <div className="grid grid-cols-1 gap-3">
                                    <div className="flex justify-between">
                                        <div className="text-sm">Gastos Generales:</div>
                                        <div className="text-sm font-semibold">{toCurrency(resultadoSimulacion.gastosPorcentaje)}</div>
                                    </div>

                                    <div className="flex justify-between">
                                        <div className="text-sm">Fondo de Reserva:</div>
                                        <div className="text-sm font-semibold">{toCurrency(resultadoSimulacion.gastosReserva)}</div>
                                    </div>

                                    <div className="flex justify-between">
                                        <div className="text-sm">Total:</div>
                                        <div className="text-sm font-semibold">{toCurrency(resultadoSimulacion.gastos)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={handleDownloadPdf}
                                    disabled={loading}
                                    className="inline-flex items-center gap-2 bg-primario-celeste-800 text-white px-4 py-2 rounded hover:bg-primario-azul-900"
                                >
                                    <FaCloudDownloadAlt /> Descargar PDF
                                </button>

                                <button
                                    onClick={handleSolicitar}
                                    disabled={loading}
                                    className="inline-flex items-center gap-2 bg-primario-celeste-800 text-white px-4 py-2 rounded hover:bg-primario-azul-900"
                                >
                                    <FaCheck /> Solicitar Préstamo
                                </button>
                            </div>

                            <div className="text-sm text-gray-500 mt-4 text-center">
                                Los montos, cuotas y fechas son estimadas y pueden ajustarse levemente una vez aprobada la solicitud.
                            </div>
                        </>
                    )}

                    {!loading && !resultadoSimulacion && !error && (
                        <div className="bg-white dark:bg-boxdark rounded p-6 text-center text-gray-600">No hay resultados para mostrar</div>
                    )}
                </div>
            </div>
        </DefaultLayout>
    );
};

export default ResultadoSimular;